import { useLatest, useToggle } from 'react-use';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { useSetServiceStateMutation } from 'apis/cm';
import { useCallback } from 'react';
import { isDeviceOnline } from 'common/utils/deviceTwinUtils';
import { hasSetServiceStatePermission } from 'components/features/MaintenancePanelBar/utils';
import { DEVICE_NOT_ONLINE_ERROR_MESSAGE, PERMISSION_ERROR_MESSAGE } from 'common/utils/constants';

function useLockUnlockGA2Device(device, options) {
  const deviceRef = useLatest(device);
  const [open, toggleOpen] = useToggle(false);
  const mutation = useSetServiceStateMutation(options);
  const rfPath = device?.type ? device.type.slice(2) : 0;
  const operationalState = get(device, 'rfPath.service-info.operational-state', 'disabled');
  const permissions = useSelector((store) => store.data?.rolePermissions?.permissions);
  const canSetServiceState = hasSetServiceStatePermission(permissions);
  const ga2Donor = useSelector((store) => {
    return store.ui.pivotOverviewPage.selectedDevices?.[device.donorId];
  });
  const isOnline = isDeviceOnline(ga2Donor);
  const operationalReadiness = get(device, 'rfPath.service-info.operational-readiness', 'not-ready');
  const operationalUpdateRequest = get(device, 'rfPath.service-info.update-req', 'idle');
  const operationalUpdateResponse = get(device, 'rfPath.service-info.update-rsp', 'idle');
  const locked = operationalState === 'disabled';
  const ready = operationalReadiness === 'normal-operation';
  const { mutate, isLoading } = mutation;
  const setServiceState = useCallback(
    ({ serviceRequest }) => {
      const { donorId } = deviceRef.current;
      toggleOpen(false);
      mutate({
        deviceId: donorId,
        rfPath: rfPath === 'all' ? rfPath : parseInt(rfPath, 10),
        serviceRequest
      });
    },
    [deviceRef, toggleOpen, mutate, rfPath]
  );
  const errors = validate({ operationalReadiness, operationalUpdateRequest, operationalUpdateResponse });

  const getActionData = () => {
    if (!isOnline) {
      return { disabled: true, message: DEVICE_NOT_ONLINE_ERROR_MESSAGE };
    }
    if (!canSetServiceState) {
      return { disabled: true, message: PERMISSION_ERROR_MESSAGE };
    }
    if (errors.length > 0) {
      return { disabled: true, message: errors[0].message };
    }
    if (isLoading) {
      return { disabled: true, message: 'Loading' };
    }

    return { disabled: false, message: undefined };
  };

  const action = getActionData();

  return {
    mutation,
    trigger: {
      onClick: toggleOpen,
      disabled: action.disabled
    },
    tooltip: {
      title: action.message
    },
    dialog: {
      open,
      locked,
      ready,
      rfPath,
      onCancel: toggleOpen,
      onConfirm: setServiceState
    }
  };
}

export default useLockUnlockGA2Device;

function validate({ operationalReadiness, operationalUpdateRequest, operationalUpdateResponse }) {
  const errors = [];

  if (operationalReadiness === 'not-ready') {
    errors.push({
      type: 'device',
      message: 'Sector is not properly configured.\nPlease execute commissioning process to configure sector'
    });
  }

  if (operationalUpdateRequest === 'in-service-request' && operationalUpdateResponse === 'in-service-pending') {
    errors.push({
      type: 'device',
      message: 'Putting sector in service...'
    });
  }

  if (operationalUpdateRequest === 'out-of-service-request' && operationalUpdateResponse === 'out-of-service-pending') {
    errors.push({
      type: 'device',
      message: 'Putting sector out of service...'
    });
  }

  return errors;
}
